import ExcelJS from 'exceljs';
import type { InventoryItem, InventorySort } from '../types/inventory';
import {
  CLEARANCE_TA_MONTHS,
  INVENTORY_ANALYSIS_TABS,
  isClearanceRow,
  isLowTa,
  LOW_TA_MONTHS,
  type InventoryAnalysisTabDef,
} from './inventoryAnalysisConfig';
import {
  fetchInventoryList,
  formatInventoryDisplayIpg,
  formatInventoryDisplayItemNo,
} from './inventoryItems';
import { downloadWorkbook } from './projectExport';

const EXPORT_PAGE_SIZE = 10000;
const ROW_HEIGHT = 40;
/** Title / date / color-code / blank, then column headers. */
const HEADER_ROW = 5;

/** Sheets that sort Turn Around high → low (clearance / obsolete stock). */
const TURN_AROUND_DESC_SHEETS = new Set(['FG-OBR', 'FG-OBX', 'CLEARANCE']);

const BORDER_THIN: Partial<ExcelJS.Border> = {
  style: 'thin',
  color: { argb: 'FFB0B0B0' },
};
const BORDER_MED: Partial<ExcelJS.Border> = {
  style: 'medium',
  color: { argb: 'FF000000' },
};

const ALIGN_CENTER: Partial<ExcelJS.Alignment> = {
  vertical: 'middle',
  horizontal: 'center',
  wrapText: true,
};

/** Core item / stock columns */
const FILL_CORE: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFD6E3F0' },
};
/** First shipment block */
const FILL_SHIP1: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFC6EFCE' },
};
/** Second shipment block */
const FILL_SHIP2: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFCE4D6' },
};
/** Monthly usage */
const FILL_USAGE: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFE2D5F1' },
};
/** Comment */
const FILL_COMMENT: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFD9D9D9' },
};

/** Portal amber — BPO TA ≥ 6 / FG-OBR / FG-OBX */
const FILL_CLEARANCE: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFF3CD' },
};

/** Portal red — BPO TA ≤ 4.5 (not OBR/OBX) */
const FILL_LOW_TA: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFF5D0CB' },
};

/** 1-based column ranges */
const COL = {
  ship1Start: 13,
  ship1End: 16,
  ship2Start: 17,
  ship2End: 20,
  usageStart: 21,
  usageEnd: 24,
  comment: 25,
  total: 25,
} as const;

const COLUMN_WIDTHS = [
  14, // Salesperson Code
  12, // Customer No.
  28, // Customer Name
  10, // IPG
  22, // Item Code
  36, // Description
  12, // Inventory
  10, // FS
  10, // FSM
  10, // TA (stock)
  10, // BPO Qty
  10, // BPO TA
  12, // 1st ETA
  10, // 1st Qty
  10, // 1st TA
  14, // Bring Forward?
  12, // 2nd ETA
  10, // 2nd Qty
  10, // 2nd TA
  14, // Bring Forward? 2
  10, // usage m1
  10, // usage m2
  10, // usage m3
  10, // usage m4
  28, // Comment
];

function buildHeaders(usageLabels: string[]): string[] {
  return [
    'Salesperson Code',
    'Customer No.',
    'Customer Name',
    'IPG',
    'Item Code',
    'Description',
    'Inventory',
    'FS',
    'FSM',
    'TA',
    'Qty',
    'TA',
    '1st ETA',
    '1st Qty',
    '1st TA',
    'Bring Forward?',
    '2nd ETA',
    '2nd Qty',
    '2nd TA',
    'Bring Forward? 2',
    usageLabels[0] ?? 'M-3',
    usageLabels[1] ?? 'M-2',
    usageLabels[2] ?? 'M-1',
    usageLabels[3] ?? 'M',
    'Comment',
  ];
}

function headerFillForCol(col: number): ExcelJS.Fill {
  if (col >= COL.ship1Start && col <= COL.ship1End) return FILL_SHIP1;
  if (col >= COL.ship2Start && col <= COL.ship2End) return FILL_SHIP2;
  if (col >= COL.usageStart && col <= COL.usageEnd) return FILL_USAGE;
  if (col === COL.comment) return FILL_COMMENT;
  return FILL_CORE;
}

function bordersForCell(
  col: number,
  rowIndex: number,
  headerRow: number,
  lastRowIndex: number,
): Partial<ExcelJS.Borders> {
  const top = rowIndex === headerRow && isShipOuterCol(col) ? BORDER_MED : BORDER_THIN;
  const bottom = rowIndex === lastRowIndex && isShipOuterCol(col) ? BORDER_MED : BORDER_THIN;
  let left = BORDER_THIN;
  let right = BORDER_THIN;
  if (col === COL.ship1Start || col === COL.ship2Start) left = BORDER_MED;
  if (col === COL.ship1End || col === COL.ship2End) right = BORDER_MED;
  return { top, left, bottom, right };
}

function isShipOuterCol(col: number): boolean {
  return (
    (col >= COL.ship1Start && col <= COL.ship1End) ||
    (col >= COL.ship2Start && col <= COL.ship2End)
  );
}

function ceilWhole(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.ceil(value);
}

function oneDecimal(value: number | null | undefined): number | '' {
  if (value == null || !Number.isFinite(value)) return '';
  return Math.round(value * 10) / 10;
}

function parseExportDate(value: string | null | undefined): Date | '' {
  if (!value) return '';
  const d = new Date(`${value.trim()}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return d;
}

function formatReportDate(d = new Date()): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/** e.g. 30/07/2026 — for export filename. */
function formatAsOfDate(d = new Date()): string {
  return formatReportDate(d);
}

/** Prefer short code: "SA1 - MK" → "SA1". */
function shortSalespersonCode(code: string): string {
  const raw = code.trim();
  if (!raw) return '';
  const cut = raw.split(/\s+-\s+/)[0]?.trim();
  return cut || raw;
}

function sanitizeFilenamePart(value: string): string {
  return value.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function buildExportFilename(selectedCodes: string[]): string {
  const asOf = formatAsOfDate();
  const shorts = [...new Set(selectedCodes.map(shortSalespersonCode).filter(Boolean))].sort(
    (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
  const prefix =
    shorts.length === 0 ? 'All' : sanitizeFilenamePart(shorts.join(' '));
  return `${prefix} Customise Items Review & Analysis - as of ${asOf}.xlsx`;
}

function applyNumFmt(cell: ExcelJS.Cell, col: number): void {
  if (col === 7 || col === 8 || col === 9 || col === 11) {
    cell.numFmt = '#,##0';
    return;
  }
  if (col === 10 || col === 12 || col === 15 || col === 19) {
    if (typeof cell.value === 'number') cell.numFmt = '0.0';
    return;
  }
  if (col === 13 || col === 17) {
    if (cell.value instanceof Date) cell.numFmt = 'dd/mm/yyyy';
    return;
  }
  if (col === 14 || col === 18 || (col >= 21 && col <= 24)) {
    cell.numFmt = '#,##0';
  }
}

function sortByTurnAround(items: InventoryItem[], descending: boolean): InventoryItem[] {
  const dir = descending ? -1 : 1;
  return [...items].sort((a, b) => {
    const aNull = a.turn_around == null || !Number.isFinite(a.turn_around);
    const bNull = b.turn_around == null || !Number.isFinite(b.turn_around);
    if (aNull && bNull) {
      return (
        a.customer_no.localeCompare(b.customer_no) || a.item_no.localeCompare(b.item_no)
      );
    }
    if (aNull) return 1;
    if (bNull) return -1;
    const diff = (a.turn_around! - b.turn_around!) * dir;
    if (diff !== 0) return diff;
    return a.customer_no.localeCompare(b.customer_no) || a.item_no.localeCompare(b.item_no);
  });
}

function styleCell(
  cell: ExcelJS.Cell,
  col: number,
  rowIndex: number,
  headerRow: number,
  lastRowIndex: number,
  rowFill?: ExcelJS.Fill,
  isHeader = false,
): void {
  cell.alignment = { ...ALIGN_CENTER };
  cell.border = bordersForCell(col, rowIndex, headerRow, lastRowIndex);
  cell.font = { name: 'Calibri', size: 10, bold: isHeader };
  if (isHeader) {
    cell.fill = headerFillForCol(col);
  } else if (rowFill) {
    cell.fill = rowFill;
  }
  if (!isHeader) applyNumFmt(cell, col);
}

function writeTitleBlock(sheet: ExcelJS.Worksheet, salespersonFilterLabel: string): void {
  const title = sheet.getCell(1, 1);
  title.value = 'Customise Items Analysis & Review';
  title.font = { name: 'Calibri', size: 16, bold: true };
  title.alignment = { vertical: 'middle', horizontal: 'left' };

  const dateCell = sheet.getCell(2, 1);
  dateCell.value = `Report Date: ${formatReportDate()}`;
  dateCell.font = { name: 'Calibri', size: 11 };
  dateCell.alignment = { vertical: 'middle', horizontal: 'left' };

  const spFilter = sheet.getCell(2, 4);
  spFilter.value = `Salesperson: ${salespersonFilterLabel}`;
  spFilter.font = { name: 'Calibri', size: 11 };
  spFilter.alignment = { vertical: 'middle', horizontal: 'left' };

  const legendLabel = sheet.getCell(3, 1);
  legendLabel.value = 'Color code:';
  legendLabel.font = { name: 'Calibri', size: 10, bold: true };
  legendLabel.alignment = { vertical: 'middle', horizontal: 'left' };

  const redSwatch = sheet.getCell(3, 2);
  redSwatch.value = '';
  redSwatch.fill = FILL_LOW_TA;
  redSwatch.border = {
    top: BORDER_THIN,
    left: BORDER_THIN,
    bottom: BORDER_THIN,
    right: BORDER_THIN,
  };

  const redText = sheet.getCell(3, 3);
  redText.value = `Red — BPO TA ≤ ${LOW_TA_MONTHS} mo (replenish)`;
  redText.font = { name: 'Calibri', size: 10 };
  redText.alignment = { vertical: 'middle', horizontal: 'left' };

  const yellowSwatch = sheet.getCell(3, 6);
  yellowSwatch.value = '';
  yellowSwatch.fill = FILL_CLEARANCE;
  yellowSwatch.border = {
    top: BORDER_THIN,
    left: BORDER_THIN,
    bottom: BORDER_THIN,
    right: BORDER_THIN,
  };

  const yellowText = sheet.getCell(3, 7);
  yellowText.value = `Yellow — BPO TA ≥ ${CLEARANCE_TA_MONTHS} mo, or FG-OBR / FG-OBX (clearance)`;
  yellowText.font = { name: 'Calibri', size: 10 };
  yellowText.alignment = { vertical: 'middle', horizontal: 'left' };

  sheet.getRow(1).height = 28;
  sheet.getRow(2).height = 20;
  sheet.getRow(3).height = 22;
  sheet.getRow(4).height = 10;
}

function addAnalysisSheet(
  workbook: ExcelJS.Workbook,
  tabDef: InventoryAnalysisTabDef,
  items: InventoryItem[],
  usageLabels: string[],
  salespersonFilterLabel: string,
): void {
  const sheet = workbook.addWorksheet(tabDef.sheetName);
  const headers = buildHeaders(usageLabels);
  const descending = TURN_AROUND_DESC_SHEETS.has(tabDef.id);
  const sorted = sortByTurnAround(items, descending);
  const lastRowIndex = sorted.length === 0 ? HEADER_ROW : HEADER_ROW + sorted.length;

  writeTitleBlock(sheet, salespersonFilterLabel);

  const headerRow = sheet.getRow(HEADER_ROW);
  headers.forEach((h, i) => {
    headerRow.getCell(i + 1).value = h;
  });
  headerRow.height = ROW_HEIGHT;
  for (let col = 1; col <= COL.total; col++) {
    styleCell(headerRow.getCell(col), col, HEADER_ROW, HEADER_ROW, lastRowIndex, undefined, true);
  }

  sorted.forEach((item, idx) => {
    const rowIndex = HEADER_ROW + 1 + idx;
    const excelRow = sheet.getRow(rowIndex);
    const values: (string | number | Date)[] = [
      item.salesperson_code || '',
      item.customer_no,
      item.customer_name,
      formatInventoryDisplayIpg(item.ipg, item.item_no),
      formatInventoryDisplayItemNo(item.item_no),
      item.description,
      ceilWhole(item.inventory),
      ceilWhole(item.fs),
      ceilWhole(item.fsm),
      oneDecimal(item.turn_around),
      ceilWhole(item.bpo),
      oneDecimal(item.ta),
      parseExportDate(item.ship1_eta),
      ceilWhole(item.ship1_qty),
      oneDecimal(item.ship1_ta),
      item.bring_forward ?? '',
      parseExportDate(item.ship2_eta),
      ceilWhole(item.ship2_qty),
      oneDecimal(item.ship2_ta),
      item.bring_forward_2 ?? '',
      ceilWhole(item.usage_m1),
      ceilWhole(item.usage_m2),
      ceilWhole(item.usage_m3),
      ceilWhole(item.usage_m4),
      item.remarks?.trim() || '',
    ];
    values.forEach((v, i) => {
      excelRow.getCell(i + 1).value = v === '' ? null : v;
    });
    excelRow.height = ROW_HEIGHT;

    const clearance = isClearanceRow(item);
    const lowTa = isLowTa(item.ta, item.ipg);
    const rowFill = lowTa ? FILL_LOW_TA : clearance ? FILL_CLEARANCE : undefined;

    for (let col = 1; col <= COL.total; col++) {
      styleCell(excelRow.getCell(col), col, rowIndex, HEADER_ROW, lastRowIndex, rowFill);
    }
  });

  COLUMN_WIDTHS.forEach((w, i) => {
    sheet.getColumn(i + 1).width = w;
  });
  sheet.views = [{ state: 'frozen', ySplit: HEADER_ROW }];
  sheet.autoFilter = {
    from: { row: HEADER_ROW, column: 1 },
    to: { row: HEADER_ROW, column: headers.length },
  };
}

function matchesSalespersonFilter(
  item: InventoryItem,
  selectedNorms: Set<string>,
): boolean {
  if (selectedNorms.size === 0) return true;
  const code = (item.salesperson_code || '').trim().toUpperCase();
  return code !== '' && selectedNorms.has(code);
}

export async function exportInventoryAnalysisExcel(params: {
  customers: string;
  search: string;
  sort: InventorySort;
  /** Empty / omitted = all salespersons. */
  salespersonCodes?: string[];
}): Promise<void> {
  const selectedNorms = new Set(
    (params.salespersonCodes ?? [])
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean),
  );
  const salespersonFilterLabel =
    selectedNorms.size === 0
      ? 'All'
      : [...selectedNorms].sort((a, b) => a.localeCompare(b)).join(', ');

  const usageLabels = ['M-3', 'M-2', 'M-1', 'M'];
  const byTab = await Promise.all(
    INVENTORY_ANALYSIS_TABS.map(async (tabDef) => {
      const result = await fetchInventoryList({
        ipg: tabDef.ipg,
        search: params.search,
        page: 1,
        pageSize: EXPORT_PAGE_SIZE,
        sort: params.sort,
        customers: params.customers,
      });
      if (result.meta.usage_labels?.length === 4) {
        usageLabels.splice(0, usageLabels.length, ...result.meta.usage_labels);
      }
      let items = result.items;
      if (tabDef.clearanceOnly) {
        items = items.filter((row) => isClearanceRow(row));
      }
      if (selectedNorms.size > 0) {
        items = items.filter((row) => matchesSalespersonFilter(row, selectedNorms));
      }
      return { tabDef, items };
    }),
  );

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'BTH Portal';
  for (const { tabDef, items } of byTab) {
    addAnalysisSheet(workbook, tabDef, items, usageLabels, salespersonFilterLabel);
  }

  await downloadWorkbook(
    workbook,
    buildExportFilename(
      selectedNorms.size === 0
        ? []
        : (params.salespersonCodes ?? []).map((c) => c.trim()).filter(Boolean),
    ),
  );
}
